import React, { useEffect, useState } from 'react';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { CookiesProvider } from 'react-cookie';
import { QueryClient, QueryClientProvider } from 'react-query';
import { RelayEnvironmentProvider } from 'react-relay/hooks';
import { FeatureFlagsProvider } from '@sparknz/feature-flags';
import {
  EnvironmentConfig,
  EnvironmentConfigProvider,
  GlobalUserProvider,
} from '@sparknz/journeys-common';
import {
  SnackbarProvider,
  SparkThemeProvider,
} from '@sparknz/spark-pattern-library';
import { ApplicationSettings } from '@sparknz/tracking';

import { defaultFlags } from 'shared/constants/featureFlags';
import CaptchaProvider from 'shared/providers/CaptchaProvider';
import { IsWebViewProvider } from 'shared/providers/isWebView';
import TrackingProvider from 'shared/providers/tracking';
import { environmentVariables } from 'shared/utils/environment.utils';
import { createRelayEnvironment, setJourneyName } from 'shared/utils/relay';
import setSentryUserData from 'shared/utils/sentry.utils';

import '@sparknz/online-web-forgerock-login/dist/index.css';

const ShadowModeLoader = dynamic(
  () => import('../shared/components/ShadowModeLoader/ShadowModeLoader'),
  {
    ssr: false, // try removing this in react 18
  },
);

const IS_PROD_ENV = process.env.NODE_ENV === 'production';

function AppInternal({ Component, pageProps }: AppProps) {
  const { graphqlURL, hCaptchaKey, apiURL } = environmentVariables;
  const { journeyName, sessionId } = pageProps;

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            suspense: false,
            retry: false,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  const environment = createRelayEnvironment({
    graphqlURL,
    sessionId,
  });

  const applicationSettings: ApplicationSettings = {
    applicationName: 'shop',
    desl: {
      withCredentials: IS_PROD_ENV,
      baseUrl: apiURL,
    },
  };

  // Setup mocks for local dev
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line global-require
      const { setupMocks } = require('shared/mocks/browser');
      setupMocks();
    }
  }, []);

  // Set user data for Sentry
  useEffect(() => {
    setSentryUserData({ journeyName });
  }, [journeyName]);

  useEffect(() => {
    setJourneyName(journeyName);
  }, [journeyName]);

  return (
    <FeatureFlagsProvider flags={defaultFlags} options={{ baseUrl: apiURL }}>
      <TrackingProvider applicationSettings={applicationSettings}>
        <IsWebViewProvider>
          <RelayEnvironmentProvider environment={environment}>
            <CookiesProvider>
              <QueryClientProvider client={queryClient}>
                <CaptchaProvider
                  hCaptchaKey={hCaptchaKey}
                  isZeroRatingEnabled={false}
                >
                  <SparkThemeProvider domain={environmentVariables.wwwUrl}>
                    <SnackbarProvider>
                      <GlobalUserProvider>
                        <Component {...pageProps} />
                        <ShadowModeLoader
                          apiURL={apiURL}
                          graphqlURL={graphqlURL}
                        />
                      </GlobalUserProvider>
                    </SnackbarProvider>
                  </SparkThemeProvider>
                </CaptchaProvider>
              </QueryClientProvider>
            </CookiesProvider>
          </RelayEnvironmentProvider>
        </IsWebViewProvider>
      </TrackingProvider>
    </FeatureFlagsProvider>
  );
}

export default function App(appProps: AppProps) {
  const envConfig: EnvironmentConfig = {
    baseURL: environmentVariables.apiURL,
    deslURL: environmentVariables.apiURL,
    graphqlURL: environmentVariables.graphqlURL,
    cartCookieName: environmentVariables.cartCookieName,
    clickScriptURL: environmentVariables.clickScriptURL,
    clickScriptIntegrity: environmentVariables.clickScriptIntegrity,
    iovationScriptURL: environmentVariables.iovationScriptURL,
    signInURL: environmentVariables.signInURL,
    cdpURL: environmentVariables.cdpURL,
    trackingURL: environmentVariables.trackingURL,
    isDevelopmentEnvironment: environmentVariables.isDevelopmentEnvironment,
    _buildID: '',
    isWebView: '',
    hCaptchaKey: environmentVariables.hCaptchaKey,
  };

  return (
    <EnvironmentConfigProvider envConfig={envConfig}>
      <AppInternal {...appProps} />
    </EnvironmentConfigProvider>
  );
}
